import { catalogFileUrl, fetchCatalogFile } from '#lib/settings/theme-catalog.js';
import type { ThemeFileSnapshot } from '#lib/platform/file-dialog-types.js';
import { readJson, writeJson } from '#lib/platform/local-json.js';
import { readThemeFile, watchesThemeFiles } from '#lib/platform/theme-files.js';

import { parseThemeFile } from './theme-file.js';

import type { ResolvedTheme } from './theme.js';

const STORAGE_KEY = 'sable-custom-themes';
const FILE_LINKS_KEY = 'sable-theme-files';
const FILE_POLL_MS = 1000;

export type CustomTheme = {
  id: string;
  name: string;
  kind: ResolvedTheme;
  css: string;
  source?: string;
};

export type CustomTweak = {
  id: string;
  name: string;
  css: string;
  source?: string;
};

export type StoredThemes = {
  themes: CustomTheme[];
  tweaks: CustomTweak[];
  lightThemeId: string | null;
  darkThemeId: string | null;
  enabledTweakIds: string[];
};

function isCustomTheme(value: unknown): value is CustomTheme {
  if (value === null || typeof value !== 'object') return false;
  const theme = value as Partial<CustomTheme>;
  return (
    typeof theme.id === 'string' &&
    typeof theme.name === 'string' &&
    (theme.kind === 'light' || theme.kind === 'dark') &&
    hasCss(theme)
  );
}

function isCustomTweak(value: unknown): value is CustomTweak {
  if (value === null || typeof value !== 'object') return false;
  const tweak = value as Partial<CustomTweak>;
  return typeof tweak.id === 'string' && typeof tweak.name === 'string' && hasCss(tweak);
}

export function isCatalogReference(entry: { source?: unknown }): boolean {
  return catalogFileUrl(entry.source) !== null;
}

function hasCss(entry: { css?: unknown; source?: unknown }): boolean {
  return typeof entry.css === 'string' || isCatalogReference(entry);
}

function withCss<T extends { css?: string }>(entry: T): T & { css: string } {
  return { ...entry, css: entry.css ?? '' };
}

export function readThemes(data: unknown): StoredThemes | null {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) return null;
  const value = data as Partial<StoredThemes>;
  if (!Array.isArray(value.themes)) return null;

  return {
    themes: value.themes.filter(isCustomTheme).map(withCss),
    tweaks: Array.isArray(value.tweaks) ? value.tweaks.filter(isCustomTweak).map(withCss) : [],
    lightThemeId: typeof value.lightThemeId === 'string' ? value.lightThemeId : null,
    darkThemeId: typeof value.darkThemeId === 'string' ? value.darkThemeId : null,
    enabledTweakIds: Array.isArray(value.enabledTweakIds)
      ? value.enabledTweakIds.filter((id: unknown): id is string => typeof id === 'string')
      : [],
  };
}

function load(): StoredThemes {
  const stored = readJson(STORAGE_KEY, readThemes, null);
  if (!stored) {
    return { themes: [], tweaks: [], lightThemeId: null, darkThemeId: null, enabledTweakIds: [] };
  }
  const held = new Set(stored.tweaks.map((tweak) => tweak.id));
  return { ...stored, enabledTweakIds: stored.enabledTweakIds.filter((id) => held.has(id)) };
}

export const customThemes = $state<StoredThemes>(load());

type ThemeFileLink = { path: string; modified: number };

function isFileLink(value: unknown): value is ThemeFileLink {
  const link = value as Partial<ThemeFileLink> | null;
  return typeof link?.path === 'string' && typeof link.modified === 'number';
}

function readFileLinks(value: unknown): Record<string, ThemeFileLink> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).filter(([, link]) => isFileLink(link)));
}

let fileLinks = readJson(FILE_LINKS_KEY, readFileLinks, {});

function persistFileLinks(): void {
  writeJson(FILE_LINKS_KEY, fileLinks, '[sable themes] theme files not persisted');
}

function pruneFileLinks(): void {
  const held = new Set([...customThemes.themes, ...customThemes.tweaks].map((entry) => entry.id));
  const kept = Object.entries(fileLinks).filter(([id]) => held.has(id));
  if (kept.length === Object.keys(fileLinks).length) return;
  fileLinks = Object.fromEntries(kept);
  persistFileLinks();
}

function persist(): void {
  writeJson(STORAGE_KEY, customThemes, '[sable themes] themes not persisted');
  pruneFileLinks();
}

export function linkThemeFile(id: string, file: ThemeFileSnapshot): void {
  fileLinks = { ...fileLinks, [id]: { path: file.path, modified: file.modified } };
  persistFileLinks();
}

function reloadThemeFile(id: string, css: string): void {
  const theme = customThemes.themes.find((entry) => entry.id === id);
  const tweak = customThemes.tweaks.find((entry) => entry.id === id);
  const current = theme ?? tweak;
  if (!current || current.css === css) return;
  const parsed = parseThemeFile(css, current.name);
  if (typeof parsed === 'string') return;
  if (theme && parsed.kind === 'theme' && parsed.theme.kind === theme.kind) {
    customThemes.themes = customThemes.themes.map((entry) =>
      entry.id === id ? { ...entry, name: parsed.theme.name, css } : entry
    );
    persist();
  } else if (tweak && parsed.kind === 'tweak') {
    customThemes.tweaks = customThemes.tweaks.map((entry) =>
      entry.id === id ? { ...entry, name: parsed.tweak.name, css } : entry
    );
    persist();
  }
}

async function pollThemeFiles(): Promise<void> {
  await Promise.all(
    Object.entries(fileLinks).map(async ([id, { path, modified }]) => {
      try {
        const file = await readThemeFile(path, modified);
        if (!file || !(id in fileLinks)) return;
        fileLinks = { ...fileLinks, [id]: { path, modified: file.modified } };
        persistFileLinks();
        reloadThemeFile(id, file.css);
      } catch (error) {
        console.debug('[sable themes] theme file not read', path, error);
      }
    })
  );
}

export function watchThemeFiles(): () => void {
  if (!watchesThemeFiles()) return () => {};
  let polling = false;
  const timer = setInterval(() => {
    if (polling || Object.keys(fileLinks).length === 0) return;
    polling = true;
    void pollThemeFiles().finally(() => {
      polling = false;
    });
  }, FILE_POLL_MS);
  return () => {
    clearInterval(timer);
  };
}

export function installCustomTheme(theme: CustomTheme, activate = true): void {
  customThemes.themes = [...customThemes.themes.filter((item) => item.id !== theme.id), theme];
  if (activate) customThemes[`${theme.kind}ThemeId`] = theme.id;
  persist();
}

export function installCustomTweak(tweak: CustomTweak): void {
  customThemes.tweaks = [...customThemes.tweaks.filter((item) => item.id !== tweak.id), tweak];
  if (!customThemes.enabledTweakIds.includes(tweak.id)) {
    customThemes.enabledTweakIds = [...customThemes.enabledTweakIds, tweak.id];
  }
  persist();
}

export function enableCustomTweak(id: string, enabled: boolean): void {
  customThemes.enabledTweakIds = enabled
    ? [...customThemes.enabledTweakIds.filter((item) => item !== id), id]
    : customThemes.enabledTweakIds.filter((item) => item !== id);
  persist();
}

export function removeCustomTheme(id: string): void {
  customThemes.themes = customThemes.themes.filter((theme) => theme.id !== id);
  if (customThemes.lightThemeId === id) customThemes.lightThemeId = null;
  if (customThemes.darkThemeId === id) customThemes.darkThemeId = null;
  persist();
}

export function removeCustomTweak(id: string): void {
  customThemes.tweaks = customThemes.tweaks.filter((tweak) => tweak.id !== id);
  customThemes.enabledTweakIds = customThemes.enabledTweakIds.filter((item) => item !== id);
  persist();
}

export function replaceCustomThemes(next: StoredThemes): void {
  customThemes.themes = next.themes;
  customThemes.tweaks = next.tweaks;
  customThemes.lightThemeId = next.lightThemeId;
  customThemes.darkThemeId = next.darkThemeId;
  customThemes.enabledTweakIds = next.enabledTweakIds;
  persist();
}

export function selectCustomTheme(kind: ResolvedTheme, id: string | null): void {
  customThemes[`${kind}ThemeId`] = id;
  persist();
}

export function selectedCustomThemeId(kind: ResolvedTheme): string | null {
  return customThemes[`${kind}ThemeId`];
}

export function activeCustomThemeCss(kind: ResolvedTheme): string | undefined {
  const id = customThemes[`${kind}ThemeId`];
  return customThemes.themes.find((theme) => theme.id === id)?.css;
}

export function activeTweakCss(): string[] {
  return customThemes.enabledTweakIds
    .map((id) => customThemes.tweaks.find((tweak) => tweak.id === id)?.css)
    .filter((css): css is string => css !== undefined);
}

export type ThemePreview = { source: string; name: string; kind: ResolvedTheme; css: string };

export const themePreview = $state<{ current: ThemePreview | null }>({ current: null });

export function previewTheme(preview: ThemePreview): void {
  themePreview.current = preview;
}

export function clearThemePreview(): void {
  themePreview.current = null;
}

let hydrating: Promise<void> | null = null;

export function hydrateCatalogThemes(): Promise<void> {
  hydrating ??= fillCatalogThemes().finally(() => {
    hydrating = null;
  });
  return hydrating;
}

async function fillCatalogThemes(): Promise<void> {
  const missing = [...customThemes.themes, ...customThemes.tweaks].filter(
    (entry) => entry.css === '' && entry.source !== undefined && isCatalogReference(entry)
  );
  if (missing.length === 0) return;

  const fetched: Record<string, string> = {};
  await Promise.all(
    missing.map(async (entry) => {
      try {
        fetched[entry.id] = await fetchCatalogFile(entry.source ?? '');
      } catch (error) {
        console.debug('[sable themes] catalog theme not fetched', entry.name, error);
      }
    })
  );
  if (Object.keys(fetched).length === 0) return;

  const fill = <T extends { id: string; css: string }>(entries: T[]): T[] =>
    entries.map((entry) => {
      const css = fetched[entry.id] as string | undefined;
      return entry.css === '' && css !== undefined ? { ...entry, css } : entry;
    });
  customThemes.themes = fill(customThemes.themes);
  customThemes.tweaks = fill(customThemes.tweaks);
  persist();
}

const CATALOG_UPDATE_INTERVAL_MS = 6 * 60 * 60_000;
let lastCatalogUpdate = 0;
let updating: Promise<void> | null = null;

export function updateCatalogThemes(): Promise<void> {
  if (updating || Date.now() - lastCatalogUpdate < CATALOG_UPDATE_INTERVAL_MS) {
    return updating ?? Promise.resolve();
  }
  updating = refreshCatalogThemes().finally(() => {
    updating = null;
  });
  return updating;
}

async function refreshCatalogThemes(): Promise<void> {
  const installed = [...customThemes.themes, ...customThemes.tweaks].filter(
    (entry) => entry.css !== '' && entry.source !== undefined && isCatalogReference(entry)
  );
  if (installed.length === 0) return;

  const fetched: Record<string, string> = {};
  await Promise.all(
    installed.map(async (entry) => {
      try {
        fetched[entry.id] = await fetchCatalogFile(entry.source ?? '', true);
      } catch (error) {
        console.debug('[sable themes] catalog theme not updated', entry.name, error);
      }
    })
  );
  if (Object.keys(fetched).length > 0) lastCatalogUpdate = Date.now();

  const refresh = <T extends { id: string; css: string }>(entries: T[]): T[] =>
    entries.map((entry) => {
      const css = fetched[entry.id] as string | undefined;
      return css !== undefined && css !== '' && css !== entry.css ? { ...entry, css } : entry;
    });
  const themes = refresh(customThemes.themes);
  const tweaks = refresh(customThemes.tweaks);
  if (
    themes.every((theme, index) => theme === customThemes.themes[index]) &&
    tweaks.every((tweak, index) => tweak === customThemes.tweaks[index])
  ) {
    return;
  }
  customThemes.themes = themes;
  customThemes.tweaks = tweaks;
  persist();
}
